import "./instrument";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { AuthKitProvider } from "@workos-inc/authkit-react";
import * as Sentry from "@sentry/react";
import { router } from "./router";
import { createIDBPersister, shouldDehydrateQuery } from "./offline/persister";
import { initializePins } from "./offline/pins";
import { registerAssetBackfill } from "./offline/asset-backfill";
import { isOnline } from "./offline/online-status";
import { markRestoreError, markRestoreSuccess } from "./offline/debug-state";
import { OfflineError } from "./lib/api";
import { resolveWorkOSAuthConfig } from "./lib/workos-auth-config";
import { initTheme } from "./lib/theme";
import { initPwaInstall } from "./lib/pwa-install";
import "./index.css";

// Before any render: apply the stored theme and start following the device
// setting. The inline script in index.html already covered first paint.
initTheme();

// Also before any render: hold the one-shot `beforeinstallprompt` event from
// the moment it fires, so the install banner and the Settings section find it
// whenever they mount. See [[install-app-banner.spec]].
initPwaInstall();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 5,
      // OfflineError is thrown synchronously from apiFetch when navigator is
      // offline — retrying just burns ~1s on backoff before surfacing the
      // same error. Treat it as terminal and let networkMode: 'offlineFirst'
      // serve cached data instead. Recover online by hook refetch / focus.
      retry: (failureCount, err) => {
        if (err instanceof OfflineError) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: true,
      networkMode: "offlineFirst",
    },
    // No global mutations.retry default. TanStack ships `retry: false` for
    // mutations and we keep it — a global default would retry non-idempotent
    // POSTs on a lost-response, silently creating duplicate projects /
    // plans / reports / invites. Each mutation hook decides individually
    // whether retry is safe for it (e.g. `useDeleteRecord` opts in because
    // DELETE is idempotent and 404 is treated as success).
  },
});

const persister = createIDBPersister({
  onError: (err, phase) => {
    markRestoreError(phase, err);
    Sentry.captureException(err, {
      tags: { area: "offline-init", phase: `persister-${phase}` },
    });
  },
});

// Populate the in-memory pinned-id cache before the persister can dehydrate.
// Without this, the first snapshot post-restore could omit pinned queries
// because `isPinnedSync` would still return false. We await, but catch any
// failure: if IDB is unavailable (private-browsing quirks, blocked storage,
// quota issues) the entire offline subsystem is dead — the app must still
// boot and serve the online path. Sentry sees the failure for monitoring.
try {
  await initializePins();
} catch (err) {
  Sentry.captureException(err, {
    tags: { area: "offline-init", phase: "initialize-pins" },
  });
}

// Keep the offline asset cache current: whenever a pinned project's record or
// plan data lands in the query cache, its images are backfilled. Registered
// before the persister restore (in render below) so hydration events are seen.
registerAssetBackfill(queryClient);

// WorkOS session persistence is env-driven — see
// specs/technical/workos-session-config.spec.md. Defaults to devMode
// (localStorage refresh token), so this is a no-op until an environment sets
// VITE_WORKOS_API_HOSTNAME (the prod first-party-cookie fix) or
// VITE_WORKOS_DEV_MODE (a testing override).
const workOSAuth = resolveWorkOSAuthConfig(import.meta.env);

createRoot(document.getElementById("root")!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn("Uncaught error", error, errorInfo.componentStack);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <AuthKitProvider
      clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
      devMode={workOSAuth.devMode}
      apiHostname={workOSAuth.apiHostname}
      onRedirectCallback={({ state }) => {
        // Return-to support. authkit-js defaults the redirect URI to the origin
        // (`/`), so by default sign-in lands the user at the app root. The
        // public share page passes `state.returnTo` when initiating sign-in so
        // the user comes back to the share link, where the auto-join flow then
        // runs (see [[share-page.spec]] US-005/US-006). No-op for every other
        // sign-in, which carries no state. Only same-origin internal paths are
        // honored: a single leading slash, rejecting the protocol-relative
        // (`//host`) and backslash (`/\host`) forms browsers resolve as
        // cross-origin — the textbook bypasses of a bare startsWith("/") check.
        const returnTo = (state as { returnTo?: unknown } | null)?.returnTo;
        if (
          typeof returnTo === "string" &&
          returnTo.startsWith("/") &&
          !returnTo.startsWith("//") &&
          !returnTo.startsWith("/\\")
        ) {
          // Deferred to a macrotask: right after this callback returns,
          // authkit-js strips `?code` with a synchronous
          // history.replaceState("/"). TanStack Router queues its own URL
          // write in a microtask and treats foreign replaceState calls as
          // navigations, so navigating inline left the router on `/` while the
          // URL bar showed returnTo — the share page never mounted and the
          // join never fired. `replace` drops the bare `/` entry from history.
          setTimeout(() => {
            void router.navigate({ to: returnTo, replace: true });
          }, 0);
        }
      }}
      onRefreshFailure={({ signIn }) => {
        // If WorkOS gives up retrying a token refresh while we're offline, do
        // NOT redirect — the offline-bypass keeps the user reading cached data
        // and the AuthGuard's online-flip handler reloads on reconnect. A
        // sign-in redirect offline would itself fail and break the experience.
        if (!isOnline()) return;
        void signIn();
      }}
    >
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          dehydrateOptions: { shouldDehydrateQuery },
          buster: "v1",
          maxAge: 1000 * 60 * 60 * 24 * 30,
        }}
        onSuccess={() => markRestoreSuccess()}
      >
        <RouterProvider router={router} />
      </PersistQueryClientProvider>
    </AuthKitProvider>
  </StrictMode>,
);
